import { createLikeableSchema, LikeableEntity } from 'mk2/containers/Like/Like.schemas';
import { createStarrableSchema, StarrableEntity } from 'mk2/containers/Star/Star.schemas';
import {
    ComplaintSchema,
    Entity,
    PhotoEntity,
    StrollerBrandEntity,
    StrollerEntity,
    StrollerVariantEntity,
    UserEntity,
} from 'mk2/schemas';
import { schema } from 'normalizr';

export interface QualityCounts {
    old: number;
    new: number;
    handmade: number;
    services: number;
    jobs: number;
}

export interface SellerEntity extends Entity {
    user: UserEntity;
    username: string; // Needed so that we can search seller by username (findDenormalizedEntityByCond)
    productsCount: number; // Number of active seller products
    reviewsCount: number;
    reviewsFromBuyersCount: number;
    reviewsFromSellersCount: number;
    hasBlockedBazaar: boolean;
    terms: string;
    location: string;
    statusMessage: string;
    likeable: LikeableEntity;
    showSellerPhone: boolean;

    // My seller data
    hasAlreadyAddedProduct?: boolean;
    transactionsCount?: number;
    myProductsCount?: number;
    availableReposts?: number;
    countyId?: number;
    placeId?: number;

    // Vendor
    vendorName?: string;
}

export const SellerLikeableSchema = createLikeableSchema('users.profile');

export const SellerSchema = new schema.Entity('bazaar.bazaar', {
    likeable: SellerLikeableSchema,
});

export enum ProductQuality {
    NEW = 'new',
    OLD = 'old',
    HANDMADE = 'handmade',
    SERVICES = 'services',
    JOBS = 'jobs', // TODO consider to clean up ProductQuality.JOBS
    ESHOP = 'eshop',
}

export enum ProductStockStatus {
    NOT_IN_STOCK,
    IN_STOCK,
}

export enum ProductPricePer {
    PER_MONTH = 1,
    PER_HOUR = 2,
}

export enum FilterType {
    BAZAAR = 'BAZAAR',
    MY_BAZAAR = 'MY_BAZAAR',
    HER_BAZAAR = 'HER_BAZAAR',
    WISHLIST = 'WISHLIST',
}

export enum RepostableStatus {
    CREATED_RECENTLY = 'created_recently', // just created - minutes
    REPOSTED_RECENTLY = 'reposted_recently', // just reposted -minutes
    NOT_REPOSTABLE = 'not_repostable', // already active, hidden, deleted category...etc
    REPOSTABLE_FREE = 'repostable_free', // free to repost
    REPOSTABLE_PAID = 'repostable_paid', // aready reposted - have wait some time (1-7 days)
    REPOSTABLE_ENTER_FACETS = 'repostable_enter_facets', // listing has to be modified - missing facets, etc
    REPOSTABLE_NOT_AVAILABLE = 'repostable_not_available', // reposts over limit - cannot repost anymore fornow
    REPOSTING = 'reposting', // reposting request pending
    REPOSTING_FAILED = 'reposting_failed', // other error of reposting
}

export enum DeletedReason {
    DELETED_REASON_COPYRIGHT_PHOTOS = 1,
    DELETED_REASON_WRONG_PRICE = 2,
    DELETED_REASON_INVISIBLE_PRODUCT = 3,
    DELETED_REASON_DONT_KNOW_WHAT_SELLING = 4,
    DELETED_REASON_NOT_FOR_TWINS = 5,
    DELETED_REASON_NOT_ALLOWED = 6,
    DELETED_REASON_MULTIPLE_PRODUCTS = 7, // Not used for new products but it must exists for legacy
    DELETED_REASON_SERVICE = 8,
    DELETED_REASON_LANGUAGE = 9,
    DELETED_REASON_NOT_ALLOWED_DUPLICATE = 10,
    DELETED_REASON_NOT_ALLOWED_DRUGS = 11,
    DELETED_REASON_NOT_ALLOWED_OPEN = 12,
    DELETED_REASON_NOT_ALLOWED_ANNOUNCEMENT = 13,
    DELETED_REASON_NOT_ALLOWED_INQUIRY = 14,
    DELETED_REASON_NOT_ALLOWED_CONTEXT = 15, // Not used for new products but it must exists for legacy
    DELETED_REASON_ANOTHER_BAZAAR = 16,
}

export interface ProductPrice {
    startTime: string;
    endTime: string;
    price: string;
}

export interface ProductPhotoFile extends Entity {
    product?: ProductEntity;
    position: number;
    photofile: PhotoEntity;
}

// keep in sync with Product.PACKAGE_SIZE_*
export enum PackageSize {
    X2 = 2,
    X3 = 3,
}

export interface ProductEntity extends Entity {
    slug: string;
    slugsHistory: string[];
    title: string;
    photos?: ProductPhotoFile[];
    seo: {
        name: string;
        description: string;
    };
    state: {
        isActive: boolean;
        isInactive: boolean;
        isExpired: boolean;
        isDeleted: boolean;
        isHidden: boolean;
    };
    packageSize: PackageSize | null;
    description: string;
    price: string;
    priceOld: string;
    pricePer: number;
    quality: ProductQuality;
    seller: SellerEntity;
    properties: Array<[string, string, string]>;
    displayProperties: string[];
    stockStatus: ProductStockStatus;
    createdTime: string;
    startTime: string;
    endTime: string | null;
    deletedReason: DeletedReason;
    prices: ProductPrice[];
    isNoads: boolean;
    starrable?: StarrableEntity;
    placeId: number;
    countyId: number;
    countryId: number;

    // My Product data
    buyersCount?: number;
    messagesCount?: number;
    newMessagesCount?: number;
    repostableStatus?: RepostableStatus;
    repostableFreeIn?: number; // Number of days

    // Pro users
    eshopUrl?: string;

    // Admins
    deletedBy?: string; // Username of admin which deleted this product
    historyCount?: number;
}

export const ProductPhotoFileSchema = new schema.Entity('bazaar.productphotofile', {});

export const ProductSchema = new schema.Entity('bazaar.product', {
    seller: SellerSchema,
    starrable: createStarrableSchema('bazaar.product'),
    photos: [ProductPhotoFileSchema],
});

ProductPhotoFileSchema.define({
    product: ProductSchema,
});

export enum ReviewRating {
    UNSATISFIED = -1,
    SATISFIED = 1,
    VERY_SATISFIED = 2,
}

export enum ReviewRole {
    BUYER = 'buyer',
    SELLER = 'seller',
}

export interface ReviewEntity extends Entity {
    date: string;
    role: ReviewRole;
    rating: ReviewRating;
    message: string;
    reviewed: UserEntity;
    reviewer: UserEntity;
    product: ProductEntity;
    photo: PhotoEntity;
    replyDate: string;
    replyMessage: string;
    replyPhoto: PhotoEntity;
    isNew: boolean;
}

export const ReviewSchema = new schema.Entity('bazaar.review', {
    product: ProductSchema,
});

export interface BuyerEntity extends Entity {
    user: UserEntity;
    product: ProductEntity;
    // TODO: Deprecated -> use seller.location
    location: string;
    seller: SellerEntity; // Seller for buyer -> not real seller as product.seller
    messagesCount: number;
    newMessagesCount: number;
    lastMessage?: BazaarMessageEntity;
    // TODO: Deprecated -> use lastMessage.sentDate
    lastMessageAt: string;
    // TODO: Deprecated -> use lastMessage.isSeller
    isLastMessageFromSeller: boolean;
}

export const BuyerSchema = new schema.Entity('bazaar.buyer', {});

export interface SellersCustomerEntity extends Entity {
    buyer: UserEntity;
    seller: UserEntity;
    notes: string;
}

export const SellersCustomerSchema = new schema.Entity('bazaar.sellerscustomer', {});

export interface BazaarMessagePhotoEntity extends Entity {
    message: BazaarMessageEntity;
    photofile: PhotoEntity;
}

export enum BazaarMessageType {
    FromBuyer = 1,
    FromSeller = 2,
    SystemReservedFromBuyer= 3,
}

export interface BazaarMessageEntity extends Entity {
    buyer: BuyerEntity;
    // TODO: Deprecated -> use buyer.seller.user
    seller: UserEntity;
    message: string;
    messageType: BazaarMessageType;
    sentDate: string;
    // TODO: Deprecated -> use messagePhotos.map((m) => m.photofile)
    photos?: PhotoEntity[];
    messagePhotos?: BazaarMessagePhotoEntity[];
    isSeller: boolean;
    isNew: boolean;
    viewDate: string;
}

export const BazaarMessagePhotoSchema = new schema.Entity('bazaar.messagephoto', {});

export const BazaarMessageSchema = new schema.Entity('bazaar.message', {
    buyer: BuyerSchema,
    messagePhotos: [BazaarMessagePhotoSchema],
});

BuyerSchema.define({
    product: ProductSchema,
    seller: SellerSchema,
    lastMessage: BazaarMessageSchema,
});

BazaarMessagePhotoSchema.define({
    message: BazaarMessageSchema,
});

export const ComplaintsPerProductSchema = new schema.Values([ComplaintSchema]);

export interface MarkEntity extends Entity {
    admin: UserEntity;
    createdTime: string;
    targetId: number;
}

export const MarkSchema = new schema.Entity('admins.adminlog', {});

export interface WatchdogEntity extends Entity {
    path: string;
    filter: any;
    isBroken: boolean;
}

export enum XMLImportStatus {
    RUNNING,
    SUCCESS,
    FAILED,
    INIT,
}

export interface XMLImportStatusEntity extends Entity {
    createdAt: string;
    updatedAt: string;
    finishedAt: string;
    log: string;
    status: XMLImportStatus;
    feedCount: number;
    deletedCount: number;
    failedCount: number;
    importedCount: number;
    repostedCount: number;
    createdCount: number;
    updatedCount: number;
    activatedCount: number;
    deactivatedCount: number;
}

export const XMLImportStatusSchema = new schema.Entity('strollers.xmlimportstatus', {});

export interface XMLImportItemEntity extends Entity {
    createdAt: string;
    updatedAt: string;
    ignoredAt: string;
    approvedAt: string;
    stroller: StrollerEntity;
    strollerBrand: StrollerBrandEntity;
    strollerVariant: StrollerVariantEntity;
    itemId: string;
    current: {
        id: string;
        category: string;
        description: string;
        eshopUrl: string;
        groupId: string;
        manufacturer: string;
        photos: string[];
        price: number;
        originalPrice: string;
        title: string;
        stock: ProductStockStatus;
    };
    xmlImportFeed: XMLImportFeedEntity;
}

export const XMLImportItemSchema = new schema.Entity('strollers.xmlimportitem');

export interface XMLImportFeedEntity extends Entity {
    eshopLogoPhoto: PhotoEntity;
    lastXmlImportStatus: XMLImportStatusEntity;
    feed: string;
    format: string;
    currency: string;
    name: string;
    categoryFilterRe: string;
    feedCount: number;
    queueCount: number;
    processedCount: number;
    ignoredCount: number;
    createdAt: string;
    updatedAt: string;
    pausedAt: string;
}

export const XMLImportFeedSchema = new schema.Entity('strollers.xmlimportfeed');
